import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import { ServerIcon } from '../utils/serverIcons';
import Logo from './Logo';
import './ApplicationForm.css';
import './ManagerManagement.css';

const formatText = (text) => {
  if (!text) return '';
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/__(.*?)__/g, '<u>$1</u>');
  return formatted;
};

const ApplicationForm = () => {
  const { code } = useParams();
  const slug = code;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [otherAnswers, setOtherAnswers] = useState({});
  const [token, setToken] = useState(null);
  const [servers, setServers] = useState([]);
  const [serverSelectOpen, setServerSelectOpen] = useState({});
  const [selectOpen, setSelectOpen] = useState({});
  const serverSelectRefs = useRef({});
  const selectRefs = useRef({});
  
  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const updateMetaTag = (name, content) => {
    let meta = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      if (name.startsWith('og:')) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  useEffect(() => {
    const publicUrl = process.env.PUBLIC_URL || '';
    const darkBg = `${publicUrl}/backgrounds/background-dark.png`;
    const lightBg = `${publicUrl}/backgrounds/background-light.png`;
    const bgUrl = theme === 'light' ? lightBg : darkBg;
    
    document.body.style.backgroundImage = `url(${bgUrl})`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundAttachment = 'fixed';
    const mobileBg = document.getElementById('mobile-bg');
    if (mobileBg) mobileBg.style.backgroundImage = `url(${bgUrl})`;
  }, [theme]);

  const saveFormState = () => {
    if (application && (currentStep > 0 || Object.keys(answers).length > 0)) {
      const stateToSave = {
        slug: slug,
        currentStep,
        currentQuestionIndex,
        answers,
        otherAnswers,
        timestamp: Date.now()
      };
      localStorage.setItem(`application_form_${slug}`, JSON.stringify(stateToSave));
    }
  };

  const loadFormState = () => {
    if (!application) return null;
    try {
      const saved = localStorage.getItem(`application_form_${slug}`);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.slug === slug && Date.now() - state.timestamp < 24 * 60 * 60 * 1000) {
          return state;
        }
      }
    } catch (e) {
      console.error('Error loading form state:', e);
    }
    return null;
  };

  const clearFormState = () => {
    localStorage.removeItem(`application_form_${slug}`);
  };

  const checkIfAlreadySubmitted = () => {
    try {
      const submitted = localStorage.getItem(`application_submitted_${slug}`);
      if (submitted) {
        const data = JSON.parse(submitted);
        if (data.slug === slug) {
          return true;
        }
      }
    } catch (e) {
      console.error('Error checking submission status:', e);
    }
    return false;
  };

  const markAsSubmitted = () => {
    try {
      const data = {
        slug: slug,
        timestamp: Date.now()
      };
      localStorage.setItem(`application_submitted_${slug}`, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving submission status:', e);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    }
    fetchApplication();
    fetchServers();
    
    return () => {
      document.title = 'GTA5RP Moderator';
      updateMetaTag('description', 'GTA5RP Moderator — система управления модераторами');
    };
  }, [slug]);
  
  useEffect(() => {
    if (application) {
      const title = application.title ? `${application.title} - Форма заявки` : 'Форма заявки - GTA5RP Moderator';
      const description = application.description || `Заполните форму заявки${application.title ? `: ${application.title}` : ''}`;
      
      document.title = title;
      updateMetaTag('description', description);
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      updateMetaTag('og:url', window.location.href);
    } else {
      document.title = 'Форма заявки - GTA5RP Moderator';
      updateMetaTag('description', 'Заполните форму заявки');
    }
  }, [application]);

  useEffect(() => {
    if (application && !success) {
      if (checkIfAlreadySubmitted()) {
        setAlreadySubmitted(true);
        return;
      }
      try {
        const saved = localStorage.getItem(`application_form_${slug}`);
        if (saved) {
          const state = JSON.parse(saved);
          if (state.slug === slug && Date.now() - state.timestamp < 24 * 60 * 60 * 1000) {
            setShowRestoreModal(true);
          }
        }
      } catch (e) {
        console.error('Error loading form state:', e);
      }
    }
  }, [application, success, slug]);

  useEffect(() => {
    if (application && !success && !showRestoreModal) {
      if (currentStep > 0 || Object.keys(answers).length > 0) {
        const stateToSave = {
          slug: slug,
          currentStep,
          currentQuestionIndex,
          answers: JSON.parse(JSON.stringify(answers)),
          otherAnswers: JSON.parse(JSON.stringify(otherAnswers)),
          timestamp: Date.now()
        };
        try {
          localStorage.setItem(`application_form_${slug}`, JSON.stringify(stateToSave));
        } catch (e) {
          console.error('Error saving form state:', e);
        }
      }
    }
  }, [currentStep, currentQuestionIndex, answers, otherAnswers, application, success, slug, showRestoreModal]);

  const handleRestoreForm = () => {
    try {
      const saved = localStorage.getItem(`application_form_${slug}`);
      if (saved) {
        const savedState = JSON.parse(saved);
        if (savedState.slug === slug && savedState.answers) {
          setAnswers(savedState.answers);
          setOtherAnswers(savedState.otherAnswers || {});
          setCurrentQuestionIndex(savedState.currentQuestionIndex ?? 0);
          setCurrentStep(savedState.currentStep ?? 0);
        }
      }
    } catch (e) {
      console.error('Error restoring form state:', e);
    }
    setShowRestoreModal(false);
  };

  const handleStartOver = () => {
    clearFormState();
    setCurrentStep(0);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setOtherAnswers({});
    setShowRestoreModal(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      Object.keys(serverSelectRefs.current).forEach(questionId => {
        if (serverSelectRefs.current[questionId] && !serverSelectRefs.current[questionId].contains(e.target)) {
          setServerSelectOpen(prev => ({ ...prev, [questionId]: false }));
        }
      });
      Object.keys(selectRefs.current).forEach(questionId => {
        if (selectRefs.current[questionId] && !selectRefs.current[questionId].contains(e.target)) {
          setSelectOpen(prev => ({ ...prev, [questionId]: false }));
        }
      });
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchServers = async () => {
    try {
      const response = await fetch('/api/servers/public');
      if (response.ok) {
        const data = await response.json();
        setServers(data);
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
    }
  };

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/applications/public/${slug}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при загрузке заявки');
      }

      const data = await response.json();
      
      if (!data.is_open) {
        setError('Приём заявок закрыт');
        setLoading(false);
        return;
      }

      setApplication(data);
      const saved = localStorage.getItem(`application_form_${slug}`);
      if (!saved) {
        const initialAnswers = {};
        data.questions.forEach(q => {
          if (q.question_type === 'checkbox') {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = '';
          }
        });
        setAnswers(initialAnswers);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (typeof value === 'string' && !value.startsWith('__OTHER__')) {
      setOtherAnswers(prev => {
        const newOtherAnswers = { ...prev };
        delete newOtherAnswers[questionId];
        return newOtherAnswers;
      });
    }
  };

  const handleCheckboxChange = (questionId, option, checked) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return {
          ...prev,
          [questionId]: [...current, option]
        };
      } else {
        return {
          ...prev,
          [questionId]: current.filter(o => o !== option)
        };
      }
    });
  };

  const getDiscordQuestions = () => {
    if (!application) return [];
    return application.questions.filter((q, index) => index < 2);
  };

  const getOtherQuestions = () => {
    if (!application) return [];
    return application.questions.filter((q, index) => index >= 2);
  };

  const getProgress = () => {
    if (!application) return 0;
    const totalSteps = 1 + 1 + getOtherQuestions().length;
    if (currentStep === 0) return 0;
    if (currentStep === 1) return 33;
    return 33 + ((currentQuestionIndex + 1) / getOtherQuestions().length) * 67;
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
    } else if (currentStep === 1) {
      const discordQuestions = getDiscordQuestions();
      for (const q of discordQuestions) {
        const answer = answers[q.id];
        if (!answer || (typeof answer === 'string' && !answer.trim())) {
          setError(`Необходимо заполнить поле: "${q.question_text}"`);
          return;
        }
      }
      setError('');
      if (getOtherQuestions().length > 0) {
        setCurrentStep(2);
        setCurrentQuestionIndex(0);
      } else {
        handleSubmit();
      }
    } else if (currentStep === 2) {
      const otherQuestions = getOtherQuestions();
      const currentQuestion = otherQuestions[currentQuestionIndex];
      if (currentQuestion.is_required) {
        const answer = answers[currentQuestion.id];
        let isValid = false;
        
        if (Array.isArray(answer)) {
          isValid = answer.length > 0;
        } else if (typeof answer === 'string') {
          if (answer.startsWith('__OTHER__:')) {
            const otherText = answer.replace('__OTHER__:', '').trim();
            isValid = otherText.length > 0;
          } else if (answer === '__OTHER__') {
            isValid = otherAnswers[currentQuestion.id]?.trim()?.length > 0;
          } else if (currentQuestion.question_type === 'scale') {
            isValid = answer.trim().length > 0 && !isNaN(parseInt(answer));
          } else {
            isValid = answer.trim().length > 0;
          }
        } else if (currentQuestion.question_type === 'scale') {
          isValid = answer !== null && answer !== undefined && answer !== '';
        }
        
        if (!isValid) {
          setError(`Необходимо ответить на обязательный вопрос: "${currentQuestion.question_text}"`);
          return;
        }
      }
      
      setError('');
      if (currentQuestionIndex < otherQuestions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 2) {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else {
        setCurrentStep(1);
      }
    } else if (currentStep === 1) {
      setCurrentStep(0);
    }
    setError('');
  };

  const handleSubmit = async () => {
    clearFormState();
    const requiredQuestions = application.questions.filter(q => q.is_required);
    for (const q of requiredQuestions) {
      const answer = answers[q.id];
      let isValid = false;
      
      if (Array.isArray(answer)) {
        isValid = answer.length > 0;
      } else       if (typeof answer === 'string') {
        if (answer.startsWith('__OTHER__:')) {
          const otherText = answer.replace('__OTHER__:', '').trim();
          isValid = otherText.length > 0;
        } else if (answer === '__OTHER__') {
          isValid = otherAnswers[q.id]?.trim()?.length > 0;
        } else if (q.question_type === 'scale') {
          isValid = answer.trim().length > 0 && !isNaN(parseInt(answer));
        } else {
          isValid = answer.trim().length > 0;
        }
      } else if (q.question_type === 'scale') {
        isValid = answer !== null && answer !== undefined && answer !== '';
      }
      
      if (!isValid) {
        setError(`Необходимо ответить на обязательный вопрос: "${q.question_text}"`);
        return;
      }
    }

    setIsSubmitting(true);
    setError('');
    const processedAnswers = { ...answers };
    Object.keys(processedAnswers).forEach(questionId => {
      const answer = processedAnswers[questionId];
      if (typeof answer === 'string' && answer.startsWith('__OTHER__:')) {
        processedAnswers[questionId] = answer.replace('__OTHER__:', '');
      } else if (answer === '__OTHER__' && otherAnswers[questionId]) {
        processedAnswers[questionId] = otherAnswers[questionId];
      }
    });

    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`/api/applications/public/${slug}/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ answers: processedAnswers })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при отправке заявки');
      }
      markAsSubmitted();
      setSuccess(true);
    } catch (err) {
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const questionId = question.id;
    const answer = answers[questionId] || '';
    const options = question.options 
      ? (typeof question.options === 'string' ? JSON.parse(question.options) : question.options)
      : [];

    return (
      <div className="application-question-block">
        <div className="application-question-card">
          <label className="application-question-label">
            {question.question_text}
            {question.is_required && <span className="required-mark"> *</span>}
          </label>
          {question.question_description && (
            <div className="application-question-description" dangerouslySetInnerHTML={{ __html: formatText(question.question_description) }}></div>
          )}

          {question.question_type === 'text' && (
            <input
              type="text"
              value={answer}
              onChange={(e) => handleAnswerChange(questionId, e.target.value)}
              required={question.is_required}
              placeholder="Введите ответ"
              className="application-form-input"
              autoFocus
            />
          )}

          {question.question_type === 'textarea' && (
            <textarea
              value={answer}
              onChange={(e) => handleAnswerChange(questionId, e.target.value)}
              required={question.is_required}
              placeholder="Введите ответ"
              rows="6"
              className="application-form-textarea"
              autoFocus
            />
          )}

          {question.question_type === 'select' && (
            <div className="server-select-wrap" ref={el => selectRefs.current[questionId] = el}>
              <div
                className={`server-select-custom ${selectOpen[questionId] ? 'open' : ''} ${!answer ? 'placeholder' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (options.length > 0) {
                    setSelectOpen(prev => ({ ...prev, [questionId]: !prev[questionId] }));
                  }
                }}
                role="combobox"
                aria-expanded={selectOpen[questionId]}
                aria-haspopup="listbox"
              >
                <span className="server-select-value">
                  {answer ? (
                    <span className="server-select-value-text">{answer}</span>
                  ) : (
                    <span className="server-select-placeholder">Выберите вариант</span>
                  )}
                </span>
                <span className="server-select-arrow">▾</span>
              </div>
              {selectOpen[questionId] && options.length > 0 && (
                <div className="server-select-dropdown" role="listbox">
                  {options.map((option, optIndex) => {
                    const isSelected = answer === option;
                    return (
                      <div
                        key={optIndex}
                        className={`server-select-option ${isSelected ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnswerChange(questionId, option);
                          setSelectOpen(prev => ({ ...prev, [questionId]: false }));
                        }}
                        role="option"
                      >
                        <span className="server-select-option-text">{option}</span>
                        {isSelected && <span className="server-select-option-check">✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {question.question_type === 'radio' && (
            <div className="application-radio-group">
              {options.map((option, optIndex) => (
                <label key={optIndex} className="application-radio-label">
                  <input
                    type="radio"
                    name={`question-${questionId}`}
                    value={option}
                    checked={answer === option}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    required={question.is_required}
                  />
                  <span>{option}</span>
                </label>
              ))}
              {question.has_other_option && (
                <div className="application-radio-other">
                  <label className="application-radio-label">
                    <input
                      type="radio"
                      name={`question-${questionId}`}
                      value="__OTHER__"
                      checked={answer === '__OTHER__' || (typeof answer === 'string' && answer.startsWith('__OTHER__'))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const currentOtherText = otherAnswers[questionId] || '';
                          if (currentOtherText) {
                            handleAnswerChange(questionId, `__OTHER__:${currentOtherText}`);
                          } else {
                            handleAnswerChange(questionId, '__OTHER__');
                          }
                        }
                      }}
                      required={question.is_required && !answer}
                    />
                    <span>Другое:</span>
                  </label>
                  {(answer === '__OTHER__' || (typeof answer === 'string' && answer.startsWith('__OTHER__'))) && (
                    <input
                      type="text"
                      value={typeof answer === 'string' && answer.startsWith('__OTHER__:') 
                        ? answer.replace('__OTHER__:', '') 
                        : (otherAnswers[questionId] || '')}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setOtherAnswers(prev => ({
                          ...prev,
                          [questionId]: newValue
                        }));
                        handleAnswerChange(questionId, `__OTHER__:${newValue}`);
                      }}
                      placeholder="Введите свой вариант"
                      className="application-form-input application-radio-other-input"
                      autoFocus
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {question.question_type === 'checkbox' && (
            <div className="application-checkbox-group">
              {options.map((option, optIndex) => (
                <label key={optIndex} className="application-checkbox-label">
                  <input
                    type="checkbox"
                    value={option}
                    checked={Array.isArray(answer) && answer.includes(option)}
                    onChange={(e) => handleCheckboxChange(questionId, option, e.target.checked)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.question_type === 'server' && (
            <div className="server-select-wrap" ref={el => serverSelectRefs.current[questionId] = el}>
              <div
                className={`server-select-custom ${serverSelectOpen[questionId] ? 'open' : ''} ${!answer ? 'placeholder' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (servers.length > 0) {
                    setServerSelectOpen(prev => ({ ...prev, [questionId]: !prev[questionId] }));
                  }
                }}
                role="combobox"
                aria-expanded={serverSelectOpen[questionId]}
                aria-haspopup="listbox"
              >
                <span className="server-select-value">
                  {answer ? (() => {
                    const s = servers.find((sv) => String(sv.id) === String(answer));
                    if (s) {
                      return (
                        <>
                          <ServerIcon server={s} className="server-select-icon" size={20} />
                          <span className="server-select-value-text">RU{s.server_number}_{s.server_name}</span>
                        </>
                      );
                    }
                    return <span className="server-select-value-text">Сервер выбран</span>;
                  })() : (
                    <span className="server-select-placeholder">Выберите сервер</span>
                  )}
                </span>
                <span className="server-select-arrow">▾</span>
              </div>
              {serverSelectOpen[questionId] && servers.length > 0 && (
                <div className="server-select-dropdown" role="listbox">
                  {servers.map((server) => {
                    const isSelected = String(answer) === String(server.id);
                    return (
                      <div
                        key={server.id}
                        className={`server-select-option ${isSelected ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnswerChange(questionId, String(server.id));
                          setServerSelectOpen(prev => ({ ...prev, [questionId]: false }));
                        }}
                        role="option"
                      >
                        <ServerIcon server={server} className="server-select-option-icon" size={20} />
                        <span className="server-select-option-text">RU{server.server_number}_{server.server_name}</span>
                        {isSelected && <span className="server-select-option-check">✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {question.question_type === 'scale' && (() => {
            const scaleOptions = question.options 
              ? (typeof question.options === 'string' ? JSON.parse(question.options) : question.options)
              : { min: 1, max: 5, label_min: '', label_max: '' };
            const min = scaleOptions.min || 1;
            const max = scaleOptions.max || 5;
            const labelMin = scaleOptions.label_min || '';
            const labelMax = scaleOptions.label_max || '';
            const scaleValues = Array.from({ length: max - min + 1 }, (_, i) => min + i);
            const selectedValue = answer ? parseInt(answer) : null;

            return (
              <div className="application-scale-group">
                <div className="application-scale-buttons">
                  {scaleValues.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`application-scale-button ${selectedValue === value ? 'selected' : ''}`}
                      onClick={() => handleAnswerChange(questionId, String(value))}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                {(labelMin || labelMax) && (
                  <div className="application-scale-labels-row">
                    <span className="application-scale-label-text application-scale-label-min">{labelMin || '\u00A0'}</span>
                    <span className="application-scale-label-text application-scale-label-max">{labelMax || '\u00A0'}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="application-form-wrapper">
        <div className="application-form-container">
          <div className="application-loading">
            <div className="application-loader"></div>
            <p>Загрузка заявки...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="application-form-wrapper">
        <div className="application-form-container">
          <div className="application-error-message">
            <h2>Ошибка</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    const completionText = application?.completion_text?.trim();
    return (
      <div className="application-form-wrapper">
        <div className="application-form-container">
          <div className="application-success-message">
            {completionText ? (
              <div className="application-completion-text">
                {completionText.split('\n').map((line, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: formatText(line) }}></p>
                ))}
              </div>
            ) : (
              <>
                <h2>Заявка успешно отправлена</h2>
                <p>Ожидайте результатов в специальном канале либо ждите тега-оповещения</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="application-form-wrapper">
        <div className="application-form-container">
          <div className="application-error-message">
            <h2>Заявка уже была отправлена</h2>
            <p>Вы уже отправили заявку на эту форму. Повторная отправка невозможна.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return null;
  }

  const discordQuestions = getDiscordQuestions();
  const otherQuestions = getOtherQuestions();
  const currentQuestion = currentStep === 2 ? otherQuestions[currentQuestionIndex] : null;
  const progress = getProgress();

  return (
    <div className="application-form-wrapper">
      {showRestoreModal && (
        <div className="application-restore-modal-overlay">
          <div className="application-restore-modal" onClick={(e) => e.stopPropagation()}>
            <div className="application-restore-modal-header">
              <h3>Восстановление формы</h3>
            </div>
            <div className="application-restore-modal-body">
              <p>Обнаружены сохраненные данные заполнения формы. Что вы хотите сделать?</p>
            </div>
            <div className="application-restore-modal-footer">
              <button 
                className="application-btn application-btn-prev"
                onClick={handleStartOver}
              >
                Начать заново
              </button>
              <button 
                className="application-btn application-btn-submit"
                onClick={handleRestoreForm}
              >
                Продолжить
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="application-form-container">
        <div className="application-form-card">
          <div className="application-form-logo">
            <Logo variant="form" />
          </div>

          <div className="application-form-header">
            <h1 className="application-form-title">{application.title}</h1>
          </div>

          {currentStep > 0 && (
            <div className="application-progress-bar-wrap">
              <div className="application-progress-bar-header">
                <span className="application-progress-label application-progress-label-start">0</span>
                <div className="application-progress-bar-container">
                  <div className="application-progress-bar" style={{ width: `${progress}%` }} />
                </div>
                <span className="application-progress-label application-progress-label-end">100</span>
              </div>
              <div className="application-progress-text">{Math.round(progress)}%</div>
            </div>
          )}

          {error && (
            <div className="application-alert application-alert-error">
              {error}
            </div>
          )}

          {currentStep === 0 && (
            <div className="application-intro-block">
              <div className="application-intro-divider" />
              {application.intro_text ? (
                <div className="application-intro-text">
                  {application.intro_text.split('\n').map((line, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ __html: formatText(line) }}></p>
                  ))}
                </div>
              ) : (
                <div className="application-intro-text">
                  <p>Добро пожаловать! Пожалуйста, внимательно заполните форму заявки.</p>
                </div>
              )}
              <button 
                type="button" 
                className="application-btn application-btn-next"
                onClick={handleNextStep}
              >
                <span>Начать заполнение</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {currentStep === 1 && (
            <div className="application-discord-block">
              {discordQuestions.map((question) => (
                <div key={question.id} className="application-question-block">
                  {renderQuestion(question)}
                </div>
              ))}
            </div>
          )}

          {currentStep === 2 && currentQuestion && (
            <div className="application-question-step">
              {renderQuestion(currentQuestion)}
            </div>
          )}

          {(currentStep === 1 || currentStep === 2) && (
            <div className="application-question-nav">
              {currentStep === 1 ? (
                <>
                  <button 
                    type="button" 
                    className="application-btn application-btn-prev"
                    onClick={handlePrevStep}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Назад</span>
                  </button>
                  <button 
                    type="button" 
                    className={`application-btn ${otherQuestions.length > 0 ? 'application-btn-next' : 'application-btn-submit'}`}
                    onClick={handleNextStep}
                  >
                    <span>{otherQuestions.length > 0 ? 'Далее' : 'Отправить'}</span>
                    {otherQuestions.length > 0 && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {currentQuestionIndex > 0 ? (
                    <button 
                      type="button" 
                      className="application-btn application-btn-prev"
                      onClick={handlePrevStep}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      <span>Предыдущий</span>
                    </button>
                  ) : (
                    <div></div>
                  )}
                  <button 
                    type="button" 
                    className={`application-btn ${currentQuestionIndex < otherQuestions.length - 1 ? 'application-btn-next' : 'application-btn-submit'}`}
                    onClick={handleNextStep}
                    disabled={isSubmitting}
                  >
                    <span>{currentQuestionIndex < otherQuestions.length - 1 ? 'Следующий' : 'Отправить заявку'}</span>
                    {currentQuestionIndex < otherQuestions.length - 1 && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ApplicationFormWithTheme = () => {
  return (
    <ThemeProvider>
      <ApplicationForm />
    </ThemeProvider>
  );
};

export default ApplicationFormWithTheme;
